import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { SchoolLayout } from "./SchoolLayout";
import { Button } from "@/project/karuna/components/ui/button"; // @ts-ignore - button is not a valid module
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/project/karuna/components/ui/card"; // @ts-ignore - card is not a valid module
import { Badge } from "@/project/karuna/components/ui/badge"; // @ts-ignore - badge is not a valid module
import { Label } from "@/project/karuna/components/ui/label"; // @ts-ignore - label is not a valid module
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/project/karuna/components/ui/tabs"; // @ts-ignore - tabs is not a valid module
import { useKarunaLink } from "@/project/karuna/utils/navigationRoutes";
import {
  ArrowLeft,
  Calendar,
  School,
  User,
  FileText,
  Loader2,
  Download,
  Image as ImageIcon,
  History,
  CheckCircle,
  XCircle,
  Award,
} from "lucide-react";
import { useToast } from "@/project/karuna/hooks/use-toast"; // @ts-ignore - use-toast is not a valid module
import { DynamicModuleService, UploadService } from "@/services/APIs";
import { useQuery } from "@tanstack/react-query";
import {
  mapWithConcurrency,
  DEFAULT_API_FETCH_CONCURRENCY,
} from "@/project/karuna/utils/mapWithConcurrency";

const SUBMISSION_SCHEMA_NAME = "Activity Submission";
const SCHOOL_PROFILE_SCHEMA_NAME = "School Profile";
const ACTIVITY_SCHEMA_NAME = "Activity Management";

// Normalize mobile number helper
const normalizeMobileNumber = (mobileNumber: string): string => {
  return String(mobileNumber ?? "")
    .replace(/\D/g, "")
    .slice(0, 10);
};

// Format date helper
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) {
    return "N/A";
  }
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return dateString;
  } // Return original if invalid

  // Format as DD-MM-YYYY
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

// Format date and time with proper timezone handling - matches system time exactly
const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) {
    return "N/A";
  }
  try {
    let date: Date;

    // Handle different date string formats
    if (typeof dateString === "string") {
      // If the string doesn't have timezone info (no Z, no +/-, no timezone offset),
      // assume it's UTC and append 'Z' to make it explicit
      // This is common for backend APIs that return dates without timezone info
      let normalizedDateString = dateString.trim();

      // Check if it already has timezone info
      const hasTimezone =
        normalizedDateString.endsWith("Z") ||
        normalizedDateString.includes("+") ||
        (normalizedDateString.includes(
          "-",
          normalizedDateString.indexOf("T") + 1
        ) &&
          normalizedDateString.match(/[+-]\d{2}:?\d{2}$/));

      // If no timezone info and it looks like an ISO date/time string, assume UTC
      if (!hasTimezone && normalizedDateString.includes("T")) {
        // It's an ISO format without timezone - assume UTC
        normalizedDateString = normalizedDateString.endsWith("Z")
          ? normalizedDateString
          : normalizedDateString + "Z";
      } else if (
        !hasTimezone &&
        normalizedDateString.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/)
      ) {
        // Format like "2025-12-16 06:02:18" - assume UTC and convert to ISO format
        normalizedDateString = normalizedDateString.replace(" ", "T") + "Z";
      }

      date = new Date(normalizedDateString);
    } else {
      date = new Date(dateString);
    }

    if (isNaN(date.getTime())) {
      console.warn("Invalid date string:", dateString);
      return "N/A";
    }

    // Format as DD-MM-YYYY, HH:MM:SS AM/PM
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    // Format time
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, "0");
    const seconds = String(date.getSeconds()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const formattedHours = String(hours).padStart(2, "0");

    return `${day}-${month}-${year}, ${formattedHours}:${minutes}:${seconds} ${ampm}`;
  } catch (error) {
    console.warn("Error formatting date:", dateString, error);
    return "N/A";
  }
};

// Helper function to resolve file ID/object to URL
const resolveFileUrl = async (fileValue: any): Promise<string | null> => {
  if (!fileValue) {
    return null;
  }

  // If it's already a URL string
  if (typeof fileValue === "string") {
    if (fileValue.startsWith("http://") || fileValue.startsWith("https://")) {
      return fileValue.startsWith("http://")
        ? fileValue.replace("http://", "https://")
        : fileValue;
    }

    // Check if it's a JSON string (starts with [ or {)
    if (fileValue.trim().startsWith("[") || fileValue.trim().startsWith("{")) {
      try {
        const parsed = JSON.parse(fileValue);
        // Recursively resolve the parsed value
        return await resolveFileUrl(parsed);
      } catch (e) {
        // If parsing fails, it's not valid JSON, continue as regular string
        console.warn("Failed to parse JSON string:", e);
      }
    }

    // If it's a file ID (simple string), fetch the URL
    try {
      const fileResponse = await UploadService.getFileById({ id: fileValue });
      return (
        fileResponse?.file_path ??
        fileResponse?.file_path_thumbnail ??
        fileResponse?.file_path_optimized ??
        fileResponse?.url ??
        null
      );
    } catch (error) {
      console.warn(`Failed to fetch file for ID ${fileValue}:`, error);
      return null;
    }
  }

  // If it's an object (from CustomFileUpload - Array<object>)
  if (typeof fileValue === "object" && fileValue !== null) {
    // Check if it's an array (banner/thumbnail/attachments/photos are Array<object>)
    if (Array.isArray(fileValue)) {
      const firstItem = fileValue[0];
      if (!firstItem) {
        return null;
      }

      if (typeof firstItem === "object" && firstItem !== null) {
        if (
          firstItem.file_path_thumbnail &&
          typeof firstItem.file_path_thumbnail === "string"
        ) {
          return firstItem.file_path_thumbnail.startsWith("http://")
            ? firstItem.file_path_thumbnail.replace("http://", "https://")
            : firstItem.file_path_thumbnail;
        }
        if (
          firstItem.file_path_optimized &&
          typeof firstItem.file_path_optimized === "string"
        ) {
          return firstItem.file_path_optimized.startsWith("http://")
            ? firstItem.file_path_optimized.replace("http://", "https://")
            : firstItem.file_path_optimized;
        }
        if (firstItem.file_path && typeof firstItem.file_path === "string") {
          return firstItem.file_path.startsWith("http://")
            ? firstItem.file_path.replace("http://", "https://")
            : firstItem.file_path;
        }
        if (firstItem.url && typeof firstItem.url === "string") {
          return firstItem.url.startsWith("http://")
            ? firstItem.url.replace("http://", "https://")
            : firstItem.url;
        }
        if (
          firstItem.id &&
          typeof firstItem.id === "string" &&
          firstItem.id.length > 0
        ) {
          if (
            !firstItem.id.startsWith("http") &&
            !firstItem.id.startsWith("[") &&
            !firstItem.id.startsWith("{")
          ) {
            return await resolveFileUrl(firstItem.id);
          }
        }
        return null;
      }
      if (typeof firstItem === "string") {
        return await resolveFileUrl(firstItem);
      }
      return null;
    }

    // Single object (not array)
    if (fileValue.file_path_thumbnail) {
      return fileValue.file_path_thumbnail.startsWith("http://")
        ? fileValue.file_path_thumbnail.replace("http://", "https://")
        : fileValue.file_path_thumbnail;
    }
    if (fileValue.file_path_optimized) {
      return fileValue.file_path_optimized.startsWith("http://")
        ? fileValue.file_path_optimized.replace("http://", "https://")
        : fileValue.file_path_optimized;
    }
    if (fileValue.file_path) {
      return fileValue.file_path.startsWith("http://")
        ? fileValue.file_path.replace("http://", "https://")
        : fileValue.file_path;
    }
    if (fileValue.url) {
      return typeof fileValue.url === "string"
        ? fileValue.url.startsWith("http://")
          ? fileValue.url.replace("http://", "https://")
          : fileValue.url
        : await resolveFileUrl(fileValue.url);
    }
    if (
      fileValue.id &&
      typeof fileValue.id === "string" &&
      fileValue.id.length > 0
    ) {
      if (
        !fileValue.id.startsWith("http") &&
        !fileValue.id.startsWith("[") &&
        !fileValue.id.startsWith("{")
      ) {
        return await resolveFileUrl(fileValue.id);
      }
    }

    return null;
  }

  return null;
};

interface MediaFile {
  id: string;
  file_url: string;
  file_type: string;
  file_size: number | null;
}

interface Publication {
  id: string;
  media_type: string;
  media_name: string | null;
  url: string | null;
  file_url: string | null;
  publication_date: string | null;
}

interface Submission {
  id: string;
  event_id: string;
  schoolcontact?: string;
  teachername?: string;
  short_description?: string;
  document_url?: string;
  status: string;
  score: number | null;
  admin_comments?: string | null;
  submitted_at?: string | null;
  reviewed_at?: string | null;
  created_at?: string;
  schools: {
    school_name: string;
    kc_no: string;
    principal_name: string;
    contact_number: string;
    email: string;
    kendra_name: string;
  };
  events: {
    title: string;
    description: string;
    start_date: string;
    end_date: string;
    location: string;
  };
  teachers: {
    name: string;
    email: string;
    mobile: string;
  } | null;
  media_files?: MediaFile[];
  publications?: Publication[];
  rawData?: any;
}

const SchoolSubmissionDetail = () => {
  const { id: submissionId } = useParams();
  const navigate = useNavigate();
  const getLink = useKarunaLink();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [submission, setSubmission] = useState<Submission | null>(null);
  const [activeTab, setActiveTab] = useState("details");
  const [resolvedFiles, setResolvedFiles] = useState<{
    photos: string[];
    photos2: string[];
    photos3: string[];
    photos4: string[];
    photos5: string[];
    descriptiondocument: string | null;
    printscancopy: string | null;
  }>({
    photos: [],
    photos2: [],
    photos3: [],
    photos4: [],
    photos5: [],
    descriptiondocument: null,
    printscancopy: null,
  });
  const [activityBannerImage, setActivityBannerImage] = useState<string | null>(
    null
  );

  // Fetch submission data
  const {
    data: submissionData,
    isLoading: submissionLoading,
    error: submissionError,
  } = useQuery({
    queryKey: [
      "school-submission-detail",
      SUBMISSION_SCHEMA_NAME,
      submissionId,
    ],
    queryFn: async () => {
      if (!submissionId) {
        return null;
      }
      try {
        const data = await DynamicModuleService.getItemById(
          SUBMISSION_SCHEMA_NAME,
          submissionId
        );
        return data?.data ?? data ?? null;
      } catch (error) {
        console.error("Error fetching submission:", error);
        throw error;
      }
    },
    enabled: !!submissionId,
  });

  // Fetch all activities from Activity Management schema with full details
  const { data: activitiesData } = useQuery({
    queryKey: ["activities-full", ACTIVITY_SCHEMA_NAME],
    queryFn: async () => {
      try {
        const activitiesList =
          await DynamicModuleService.getGridColItemsAllPages(
            ACTIVITY_SCHEMA_NAME
          );

        const activitiesWithFullData = await mapWithConcurrency(
          activitiesList,
          DEFAULT_API_FETCH_CONCURRENCY,
          async (activity: any) => {
            const activityId = activity.id ?? activity._id;
            if (!activityId) {
              return activity;
            }

            try {
              const fullData = await DynamicModuleService.getItemById(
                ACTIVITY_SCHEMA_NAME,
                activityId
              );
              const data = fullData?.data ?? fullData ?? activity;
              return { ...activity, ...data };
            } catch (err) {
              console.warn(
                `Could not fetch full data for activity ${activityId}:`,
                err
              );
              return activity;
            }
          }
        );

        return activitiesWithFullData;
      } catch (error) {
        console.error("Error fetching activities:", error);
        return [];
      }
    },
  });

  // Fetch school profiles
  const { data: schoolProfilesData } = useQuery({
    queryKey: ["school-profiles", SCHOOL_PROFILE_SCHEMA_NAME],
    queryFn: async () => {
      try {
        return await DynamicModuleService.getGridColItemsAllPages(
          SCHOOL_PROFILE_SCHEMA_NAME
        );
      } catch (error) {
        console.error("Error fetching school profiles:", error);
        return [];
      }
    },
  });

  // Transform and set submission data
  useEffect(() => {
    const processSubmission = async () => {
      if (submissionLoading) {
        setLoading(true);
        return;
      }

      if (submissionError) {
        setLoading(false);
        toast({
          title: "Error",
          description: "Failed to load submission details",
          variant: "destructive",
        });
        return;
      }

      if (!submissionData) {
        setLoading(false);
        return;
      }

      try {
        const submission = submissionData as any;

        // Get program name from submission
        const programValue = submission.program ?? "";
        const programNameMap: Record<string, string> = {
          intraschool: "Organising Intra School Programmes",
          interschool: "Organising Inter School Programmes",
          participationinter:
            "Participation in Inter-School Competitions/Programmes",
          publicrelation: "Public Relation Programmes",
          karunaclub: "Karuna Club – Internal / External Growth",
          nationallevel:
            "Organising / Participation in National Level Programme",
        };
        const programName = programValue
          ? (programNameMap[programValue] ?? programValue)
          : "";

        // Try to find matching activity
        const activityId =
          submission.event_id ??
          submission.eventid ??
          submission.activity_id ??
          submission.activityid ??
          submission.activity ??
          "";

        let activity: any = null;
        if (activitiesData && activitiesData.length > 0) {
          if (activityId) {
            activity = activitiesData.find((act: any) => {
              const actId = String(act.id ?? act._id ?? "").trim();
              return actId === String(activityId).trim();
            });
          }

          if (!activity && programName) {
            activity = activitiesData.find((act: any) => {
              const actTitle = act.title ?? act.activitytitle ?? act.name ?? "";
              const actTitleLower = actTitle.toLowerCase();
              const programNameLower = programName.toLowerCase();
              const titleIncludesProgram =
                actTitleLower.includes(programNameLower);
              const programIncludesTitle =
                programNameLower.includes(actTitleLower);
              return titleIncludesProgram ? true : programIncludesTitle;
            });
          }
        }

        // Find school profile by contact
        const submissionContact = normalizeMobileNumber(
          submission.schoolcontact ??
            submission.contact ??
            submission.school_contact ??
            ""
        );
        const schoolProfile = schoolProfilesData?.find((profile: any) => {
          const profileContact = normalizeMobileNumber(
            profile.contact ??
              profile.school_contact ??
              profile.schoolcontact ??
              ""
          );
          return profileContact === submissionContact && profileContact !== "";
        });

        // Transform to expected structure
        const transformedSubmission: Submission = {
          id: submission.id ?? submission._id ?? "",
          event_id: "",
          schoolcontact: submissionContact,
          teachername: submission.teachername,
          short_description:
            submission.short_description ?? submission.description,
          document_url:
            submission.document_url ??
            submission.descriptiondocument ??
            submission.documenturl,
          status: submission.status ?? "pending",
          score: submission.score ?? null,
          admin_comments:
            submission.admin_comments ??
            submission.admincomments ??
            submission.comments ??
            null,
          submitted_at:
            submission.submitted_at ??
            submission.created_on ??
            submission.created_at,
          reviewed_at: submission.reviewed_at ?? submission.reviewed_on,
          created_at:
            submission.created_at ??
            submission.created_on ??
            submission.submitted_at,
          rawData: submission,
          schools: {
            school_name:
              schoolProfile?.schoolname ??
              submission.schoolname ??
              "Unknown School",
            kc_no: schoolProfile?.kcno ?? submission.kcno ?? "N/A",
            principal_name:
              schoolProfile?.principalname ?? submission.principalname ?? "N/A",
            contact_number: submissionContact ?? "N/A",
            email:
              schoolProfile?.email ??
              submission.schoolemail ??
              submission.email ??
              "N/A",
            kendra_name:
              schoolProfile?.kendra ?? submission.nearestkendra ?? "N/A",
          },
          events: {
            // Priority: submission's own title/location fields > activity fields > program name
            title:
              submission.title ??
              submission.event_title ??
              submission.activity_title ??
              submission.activitytitle ??
              activity?.title ??
              activity?.activitytitle ??
              activity?.name ??
              programName ??
              "Unknown Activity",
            description:
              submission.description ??
              submission.event_description ??
              activity?.description ??
              activity?.desc ??
              "",
            start_date:
              submission.start_date ??
              submission.startdate ??
              submission.event_start_date ??
              activity?.start_date ??
              activity?.startdate ??
              activity?.startDate ??
              activity?.start ??
              "",
            end_date:
              submission.end_date ??
              submission.enddate ??
              submission.event_end_date ??
              activity?.end_date ??
              activity?.enddate ??
              activity?.endDate ??
              activity?.end ??
              "",
            location:
              submission.location ??
              submission.event_location ??
              submission.venue ??
              submission.place ??
              activity?.location ??
              activity?.venue ??
              activity?.place ??
              activity?.location_name ??
              "",
          },
          teachers: submission.teachername
            ? {
                name: submission.teachername,
                email: submission.teacheremail ?? "",
                mobile: submission.teachermobile ?? "",
              }
            : null,
          media_files: submission.media_files ?? [],
          publications: submission.publications ?? [],
        };

        setSubmission(transformedSubmission);

        // Resolve file IDs to URLs for all 5 photo fields
        // Handle both string IDs and arrays
        const resolvePhotos = async (photoValue: any): Promise<string[]> => {
          const resolved: string[] = [];

          // If it's a string (single ID), convert to array
          if (typeof photoValue === "string" && photoValue.trim() !== "") {
            const url = await resolveFileUrl(photoValue);
            if (url) {
              resolved.push(url);
            }
          }
          // If it's an array, process each item
          else if (photoValue && Array.isArray(photoValue)) {
            for (const photo of photoValue) {
              const url = await resolveFileUrl(photo);
              if (url) {
                resolved.push(url);
              }
            }
          }

          return resolved;
        };

        // Access photos from raw submission data
        // Photos can be stored as string IDs or arrays
        const resolvedPhotos = await resolvePhotos(submission.photos);
        const resolvedPhotos2 = await resolvePhotos(submission.photos2);
        const resolvedPhotos3 = await resolvePhotos(submission.photos3);
        const resolvedPhotos4 = await resolvePhotos(submission.photos4);
        const resolvedPhotos5 = await resolvePhotos(submission.photos5);

        console.log("📸 Photo resolution:", {
          resolvedCounts: {
            photos: resolvedPhotos.length,
            photos2: resolvedPhotos2.length,
            photos3: resolvedPhotos3.length,
            photos4: resolvedPhotos4.length,
            photos5: resolvedPhotos5.length,
          },
          rawData: {
            photos: submission.photos,
            photos2: submission.photos2,
            photos3: submission.photos3,
            photos4: submission.photos4,
            photos5: submission.photos5,
          },
          submissionKeys: Object.keys(submission),
        });

        const descriptionDocUrl = await resolveFileUrl(
          submission.descriptiondocument
        );
        const printScanCopyUrl = await resolveFileUrl(submission.printscancopy);

        setResolvedFiles({
          photos: resolvedPhotos,
          photos2: resolvedPhotos2,
          photos3: resolvedPhotos3,
          photos4: resolvedPhotos4,
          photos5: resolvedPhotos5,
          descriptiondocument: descriptionDocUrl,
          printscancopy: printScanCopyUrl,
        });

        // Resolve banner image - check submission's banner first, then activity's banner
        // Priority: submission.banner > submission.banner_image > activity.banner > activity.thumbnail
        let bannerUrl: string | null = null;

        // First, check if submission has its own banner field
        const submissionBannerValue =
          submission.banner ?? submission.banner_image ?? submission.banner_url;

        if (submissionBannerValue) {
          try {
            bannerUrl = await resolveFileUrl(submissionBannerValue);
            if (bannerUrl) {
              console.log("✅ Submission banner resolved:", bannerUrl);
            }
          } catch (error) {
            console.warn("⚠️ Failed to resolve submission banner:", error);
          }
        }

        // If no submission banner, try activity banner
        if (!bannerUrl && activity) {
          const activityBannerValue =
            activity.banner ?? activity.banner_url ?? activity.banner_image;
          if (activityBannerValue) {
            try {
              bannerUrl = await resolveFileUrl(activityBannerValue);
              if (bannerUrl) {
                console.log("✅ Activity banner resolved:", bannerUrl);
              }
            } catch (error) {
              console.warn("⚠️ Failed to resolve activity banner:", error);
            }
          } else {
            // Fallback to thumbnail if banner not available
            const thumbnailValue = activity.thumbnail;
            if (thumbnailValue) {
              try {
                bannerUrl = await resolveFileUrl(thumbnailValue);
                if (bannerUrl) {
                  console.log(
                    "✅ Activity thumbnail used as banner:",
                    bannerUrl
                  );
                }
              } catch (error) {
                console.warn("⚠️ Failed to resolve activity thumbnail:", error);
              }
            }
          }
        }

        if (bannerUrl) {
          setActivityBannerImage(bannerUrl);
        }

        setLoading(false);
      } catch (error) {
        console.error("Error transforming submission data:", error);
        toast({
          title: "Error",
          description: "Failed to load submission details",
          variant: "destructive",
        });
        setLoading(false);
      }
    };

    processSubmission();
  }, [
    submissionData,
    submissionLoading,
    submissionError,
    activitiesData,
    schoolProfilesData,
    toast,
  ]);

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      pending: { color: "bg-yellow-500", label: "Pending" },
      approved: { color: "bg-green-500", label: "Approved" },
      rejected: { color: "bg-red-500", label: "Rejected" },
    };

    const config =
      statusConfig[status as keyof typeof statusConfig] ?? statusConfig.pending;

    return (
      <Badge className={`${config.color} text-white`}>{config.label}</Badge>
    );
  };

  if (loading) {
    return (
      <SchoolLayout>
        <div className="flex min-h-[400px] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </SchoolLayout>
    );
  }

  if (!submission) {
    return (
      <SchoolLayout>
        <div className="py-12 text-center">
          <h2 className="text-2xl font-bold">Submission Not Found</h2>
          <Button
            className="mt-4"
            onClick={() => navigate(getLink("/school/submissions"))}
          >
            Back to Submissions
          </Button>
        </div>
      </SchoolLayout>
    );
  }

  // Get banner image from Activity Management schema (priority) or fallback to submission photos
  const allPhotos = [
    ...resolvedFiles.photos,
    ...resolvedFiles.photos2,
    ...resolvedFiles.photos3,
    ...resolvedFiles.photos4,
    ...resolvedFiles.photos5,
  ];
  const bannerImage =
    activityBannerImage ??
    (submission
      ? allPhotos.length > 0
        ? allPhotos[0]
        : submission.media_files?.find((m: MediaFile) =>
            m.file_type.startsWith("image/")
          )?.file_url
      : null);

  return (
    <SchoolLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate(getLink("/school/submissions"))}
              className="shrink-0"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold">Submission Details</h1>
              <p className="mt-1 text-lg text-muted-foreground">
                Activity Title:{" "}
                <span className="font-bold">{submission.events.title}</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {submission.status !== "pending" && submission.score && (
              <div className="flex items-center gap-2 rounded-lg bg-primary/10 px-4 py-2">
                <Award className="h-4 w-4 text-primary" />
                <span className="font-semibold text-primary">
                  {submission.score}/100
                </span>
              </div>
            )}
            {getStatusBadge(submission.status)}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <Card className="border-border/50">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="rounded-lg bg-blue-100 p-2 dark:bg-blue-900/20">
                <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Submitted</p>
                <p className="font-semibold">
                  {submission.submitted_at
                    ? formatDate(submission.submitted_at)
                    : "N/A"}
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="rounded-lg bg-purple-100 p-2 dark:bg-purple-900/20">
                <School className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">KC Number</p>
                <p className="font-semibold">{submission.schools.kc_no}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="rounded-lg bg-green-100 p-2 dark:bg-green-900/20">
                <ImageIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Media Files</p>
                <p className="font-semibold">
                  {resolvedFiles.photos.length +
                    resolvedFiles.photos2.length +
                    resolvedFiles.photos3.length +
                    resolvedFiles.photos4.length +
                    resolvedFiles.photos5.length +
                    (submission.media_files?.length ?? 0)}{" "}
                  files
                </p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/50">
            <CardContent className="flex items-center gap-3 p-4">
              <div className="rounded-lg bg-orange-100 p-2 dark:bg-orange-900/20">
                <FileText className="h-5 w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Publications</p>
                <p className="font-semibold">
                  {submission.publications?.length ?? 0} entries
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 lg:w-auto">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="media">Media</TabsTrigger>
            <TabsTrigger value="publications">Publications</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>

          <div className="mt-6 space-y-6">
            <TabsContent value="details" className="mt-0 space-y-6">
              {/* Banner Image from Activity Management */}
              {bannerImage && (
                <div className="relative h-64 w-full overflow-hidden rounded-lg">
                  <img
                    src={bannerImage}
                    alt="Activity banner"
                    className="h-full w-full object-cover"
                    onError={e => {
                      console.error(
                        `Failed to load banner image: ${bannerImage}`
                      );
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/40 to-transparent" />
                  <div className="absolute bottom-6 left-6 text-white">
                    <h2 className="text-2xl font-bold drop-shadow-lg">
                      {submission.events.title}
                    </h2>
                    {submission.events.location && (
                      <p className="mt-1 text-base opacity-95 drop-shadow-md">
                        {submission.events.location}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Submission Report */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Submission Report
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-muted-foreground">
                    {submission.rawData?.description ??
                      submission.short_description ??
                      "No description provided"}
                  </p>
                  {(resolvedFiles.descriptiondocument ??
                    submission.document_url ??
                    submission.rawData?.descriptiondocument) && (
                    <Button
                      variant="outline"
                      className="w-full sm:w-auto"
                      onClick={() =>
                        window.open(
                          resolvedFiles.descriptiondocument ??
                            submission.document_url ??
                            submission.rawData?.descriptiondocument ??
                            "",
                          "_blank"
                        )
                      }
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Download Full Report
                    </Button>
                  )}
                </CardContent>
              </Card>

              {/* Section A: Basic School & Contact Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <School className="h-5 w-5" />
                    Section A: Basic School & Contact Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <Label className="text-muted-foreground">KC No</Label>
                      <p className="mt-1 font-medium">
                        {submission.rawData?.kcno ?? submission.schools.kc_no}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">
                        School Name
                      </Label>
                      <p className="mt-1 font-medium">
                        {submission.rawData?.schoolname ??
                          submission.schools.school_name}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">
                        Nearest Karuna Kendra Name
                      </Label>
                      <p className="mt-1">
                        {submission.rawData?.nearestkendra ??
                          submission.schools.kendra_name}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">
                        School Principal&apos;s Name
                      </Label>
                      <p className="mt-1">
                        {submission.rawData?.principalname ??
                          submission.schools.principal_name}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">
                        School Contact Number
                      </Label>
                      <p className="mt-1">
                        {submission.rawData?.schoolcontact ??
                          submission.schools.contact_number}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">
                        School Email ID
                      </Label>
                      <p className="mt-1">
                        {submission.rawData?.schoolemail ??
                          submission.schools.email}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Section B: Karuna Club Teacher In-Charge */}
              {submission.rawData?.teachername && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5" />
                      Section B: Karuna Club Teacher In-Charge
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                      <div>
                        <Label className="text-muted-foreground">Name</Label>
                        <p className="mt-1">
                          {submission.rawData?.teachername ??
                            submission.teachers?.name}
                        </p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">
                          Mobile Number
                        </Label>
                        <p className="mt-1">
                          {submission.rawData?.teachermobile ??
                            submission.teachers?.mobile}
                        </p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground">
                          Email ID
                        </Label>
                        <p className="mt-1">
                          {submission.rawData?.teacheremail ??
                            submission.teachers?.email}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Section C: Event Program Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Section C: Event Program Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <Label className="text-muted-foreground">
                        Select State
                      </Label>
                      <p className="mt-1">
                        {submission.rawData?.state
                          ? submission.rawData.state
                              .split("_")
                              .map(
                                (w: string) =>
                                  w.charAt(0).toUpperCase() + w.slice(1)
                              )
                              .join(" ")
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">
                        Select Program
                      </Label>
                      <p className="mt-1 font-medium">
                        {(() => {
                          const programValue =
                            submission.rawData?.program ?? "";
                          const programNameMap: Record<string, string> = {
                            intraschool: "Organising Intra School Programmes",
                            interschool: "Organising Inter School Programmes",
                            participationinter:
                              "Participation in Inter-School Competitions/Programmes",
                            publicrelation: "Public Relation Programmes",
                            karunaclub:
                              "Karuna Club – Internal / External Growth",
                            nationallevel:
                              "Organising / Participation in National Level Programme",
                          };
                          return programValue
                            ? (programNameMap[programValue] ?? programValue)
                            : "N/A";
                        })()}
                      </p>
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-muted-foreground">
                        Short Description
                      </Label>
                      <p className="mt-1 whitespace-pre-wrap">
                        {submission.rawData?.description ??
                          submission.short_description ??
                          "N/A"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="media" className="mt-0 space-y-6">
              {/* Section D: Media Uploads */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <ImageIcon className="h-5 w-5" />
                    Section D: Media Uploads
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Photos of Program (4-5 photos required)
                  </p>
                </CardHeader>
                <CardContent>
                  {(() => {
                    // Combine all photos from all 5 fields
                    const allPhotos = [
                      ...resolvedFiles.photos,
                      ...resolvedFiles.photos2,
                      ...resolvedFiles.photos3,
                      ...resolvedFiles.photos4,
                      ...resolvedFiles.photos5,
                    ];

                    return allPhotos.length > 0 ? (
                      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
                        {allPhotos.map((photoUrl: string, index: number) => (
                          <div
                            key={photoUrl}
                            className="group relative aspect-video overflow-hidden rounded-lg border"
                          >
                            <img
                              src={photoUrl}
                              alt={`${index + 1}`}
                              className="h-full w-full object-cover transition-transform group-hover:scale-105"
                              onError={e => {
                                console.error(
                                  `Failed to load image: ${photoUrl}`
                                );
                                (e.target as HTMLImageElement).style.display =
                                  "none";
                              }}
                            />
                            <a
                              href={photoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 transition-opacity group-hover:opacity-100"
                            >
                              <Download className="h-8 w-8 text-white" />
                            </a>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="py-8 text-center text-muted-foreground">
                        No photos uploaded
                      </p>
                    );
                  })()}
                </CardContent>
              </Card>

              {/* Video Links */}
              {[
                submission.rawData?.videolink1,
                submission.rawData?.videolink2,
                submission.rawData?.videolink3,
              ].some(Boolean) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ImageIcon className="h-5 w-5" />
                      Video Links
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Additional video documentation
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                      {submission.rawData?.videolink1 && (
                        <Card className="p-4">
                          <Label className="text-muted-foreground">
                            Video Link 1
                          </Label>
                          <a
                            href={submission.rawData.videolink1}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-1 block truncate text-primary hover:underline"
                          >
                            {submission.rawData.videolink1}
                          </a>
                        </Card>
                      )}
                      {submission.rawData?.videolink2 && (
                        <Card className="p-4">
                          <Label className="text-muted-foreground">
                            Video Link 2
                          </Label>
                          <a
                            href={submission.rawData.videolink2}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-1 block truncate text-primary hover:underline"
                          >
                            {submission.rawData.videolink2}
                          </a>
                        </Card>
                      )}
                      {submission.rawData?.videolink3 && (
                        <Card className="p-4">
                          <Label className="text-muted-foreground">
                            Video Link 3
                          </Label>
                          <a
                            href={submission.rawData.videolink3}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-1 block truncate text-primary hover:underline"
                          >
                            {submission.rawData.videolink3}
                          </a>
                        </Card>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Section E: Print Media - Scan Copy */}
              {resolvedFiles.printscancopy && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <FileText className="h-5 w-5" />
                      Section E: Publication Details – Print Media
                    </CardTitle>
                    <p className="text-sm text-muted-foreground">
                      Scan Copy of Publication (PDF/Image)
                    </p>
                  </CardHeader>
                  <CardContent>
                    <div className="rounded-lg border p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <FileText className="h-8 w-8 text-primary" />
                          <div>
                            <p className="font-medium">
                              Scan Copy of Publication
                            </p>
                            <p className="text-sm text-muted-foreground">
                              PDF/Image file
                            </p>
                          </div>
                        </div>
                        <a
                          href={resolvedFiles.printscancopy}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:text-primary/80"
                        >
                          <Download className="h-5 w-5" />
                        </a>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="publications" className="mt-0">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Publication Details
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Media coverage and publications
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Section E: Print Media */}
                  {(submission.rawData?.publicationdate ??
                    submission.rawData?.websiteurl ??
                    submission.rawData?.printscancopy) && (
                    <div>
                      <Label className="mb-3 text-base font-semibold">
                        Section E: Print Media
                      </Label>
                      <Card className="p-4">
                        <div className="space-y-3">
                          {submission.rawData?.publicationdate && (
                            <div>
                              <Label className="text-muted-foreground">
                                Date of Publication
                              </Label>
                              <p className="mt-1">
                                {formatDate(submission.rawData.publicationdate)}
                              </p>
                            </div>
                          )}
                          {submission.rawData?.websiteurl && (
                            <div>
                              <Label className="text-muted-foreground">
                                Website URL Link
                              </Label>
                              <a
                                href={submission.rawData.websiteurl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-1 block text-primary hover:underline"
                              >
                                {submission.rawData.websiteurl}
                              </a>
                            </div>
                          )}
                          {resolvedFiles.printscancopy && (
                            <div className="flex items-center justify-between rounded-lg border p-3">
                              <div className="flex items-center gap-3">
                                <FileText className="h-6 w-6 text-primary" />
                                <div>
                                  <p className="font-medium">
                                    Scan Copy of Publication
                                  </p>
                                  <p className="text-sm text-muted-foreground">
                                    PDF/Image file
                                  </p>
                                </div>
                              </div>
                              <a
                                href={resolvedFiles.printscancopy}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:text-primary/80"
                              >
                                <Download className="h-5 w-5" />
                              </a>
                            </div>
                          )}
                        </div>
                      </Card>
                    </div>
                  )}

                  {/* Section F: TV Channel */}
                  {(submission.rawData?.tvchannelname ??
                    submission.rawData?.tvtelecastdate ??
                    submission.rawData?.tvprogramurl ??
                    submission.rawData?.tvProgramVideo) && (
                    <div>
                      <Label className="mb-3 text-base font-semibold">
                        Section F: TV Channel
                      </Label>
                      <Card className="p-4">
                        <div className="space-y-3">
                          {submission.rawData?.tvchannelname && (
                            <div>
                              <Label className="text-muted-foreground">
                                TV Channel Name
                              </Label>
                              <p className="mt-1 font-medium">
                                {submission.rawData.tvchannelname}
                              </p>
                            </div>
                          )}
                          {submission.rawData?.tvtelecastdate && (
                            <div>
                              <Label className="text-muted-foreground">
                                Program Telecast Date
                              </Label>
                              <p className="mt-1">
                                {formatDate(submission.rawData.tvtelecastdate)}
                              </p>
                            </div>
                          )}
                          {submission.rawData?.tvprogramurl && (
                            <div>
                              <Label className="text-muted-foreground">
                                Program URL
                              </Label>
                              <a
                                href={submission.rawData.tvprogramurl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-1 block text-primary hover:underline"
                              >
                                {submission.rawData.tvprogramurl}
                              </a>
                            </div>
                          )}
                        </div>
                      </Card>
                    </div>
                  )}

                  {/* Section G: Social Media Links */}
                  {[
                    submission.rawData?.link1,
                    submission.rawData?.link2,
                    submission.rawData?.link3,
                    submission.rawData?.link4,
                  ].some(Boolean) && (
                    <div>
                      <Label className="mb-3 text-base font-semibold">
                        Section G: Social Media Links
                      </Label>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        {submission.rawData?.link1 && (
                          <Card className="p-4">
                            <Label className="text-muted-foreground">
                              Link 1
                            </Label>
                            <a
                              href={submission.rawData.link1}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-1 block truncate text-primary hover:underline"
                            >
                              {submission.rawData.link1}
                            </a>
                          </Card>
                        )}
                        {submission.rawData?.link2 && (
                          <Card className="p-4">
                            <Label className="text-muted-foreground">
                              Link 2
                            </Label>
                            <a
                              href={submission.rawData.link2}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-1 block truncate text-primary hover:underline"
                            >
                              {submission.rawData.link2}
                            </a>
                          </Card>
                        )}
                        {submission.rawData?.link3 && (
                          <Card className="p-4">
                            <Label className="text-muted-foreground">
                              Link 3
                            </Label>
                            <a
                              href={submission.rawData.link3}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-1 block truncate text-primary hover:underline"
                            >
                              {submission.rawData.link3}
                            </a>
                          </Card>
                        )}
                        {submission.rawData?.link4 && (
                          <Card className="p-4">
                            <Label className="text-muted-foreground">
                              Link 4
                            </Label>
                            <a
                              href={submission.rawData.link4}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-1 block truncate text-primary hover:underline"
                            >
                              {submission.rawData.link4}
                            </a>
                          </Card>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Legacy publications fallback */}
                  {submission.publications &&
                    submission.publications.length > 0 && (
                      <div>
                        <Label className="mb-3 text-base font-semibold">
                          Additional Publications
                        </Label>
                        <div className="space-y-3">
                          {submission.publications.map(pub => (
                            <Card
                              key={pub.id}
                              className="p-4 transition-shadow hover:shadow-md"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1 space-y-1">
                                  <p className="font-medium">
                                    {pub.media_name ?? pub.media_type}
                                  </p>
                                  {pub.publication_date && (
                                    <p className="text-sm text-muted-foreground">
                                      Published:{" "}
                                      {formatDate(pub.publication_date)}
                                    </p>
                                  )}
                                  {pub.url && (
                                    <a
                                      href={pub.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="block text-sm text-primary hover:underline"
                                    >
                                      View Publication
                                    </a>
                                  )}
                                </div>
                                {pub.file_url && (
                                  <a
                                    href={pub.file_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary hover:text-primary/80"
                                  >
                                    <Download className="h-5 w-5" />
                                  </a>
                                )}
                              </div>
                            </Card>
                          ))}
                        </div>
                      </div>
                    )}

                  {!submission.rawData?.publicationdate &&
                    !submission.rawData?.websiteurl &&
                    !submission.rawData?.printscancopy &&
                    !submission.rawData?.tvchannelname &&
                    !submission.rawData?.tvtelecastdate &&
                    !submission.rawData?.tvprogramurl &&
                    (!submission.publications ||
                      submission.publications.length === 0) && (
                      <p className="text-center text-muted-foreground">
                        No publications available
                      </p>
                    )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="history" className="mt-0">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <History className="h-5 w-5" />
                    Submission History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="relative space-y-6">
                    {/* Timeline */}
                    <div className="relative">
                      {/* Submission Created */}
                      <div className="relative flex gap-4 pb-6">
                        <div className="flex shrink-0 flex-col items-center">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/20">
                            <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          {/* Only show line if there are more items */}
                          {((submission.submitted_at !== null &&
                            submission.created_at !== null &&
                            submission.submitted_at !==
                              submission.created_at) ||
                            submission.status === "approved" ||
                            submission.status === "rejected") && (
                            <div className="absolute left-1/2 top-10 h-6 w-0.5 -translate-x-1/2 bg-border" />
                          )}
                        </div>
                        <div className="flex-1 pb-6">
                          <p className="font-semibold">Submission Created</p>
                          <p className="text-sm text-muted-foreground">
                            {formatDateTime(submission.created_at)}
                          </p>
                        </div>
                      </div>

                      {/* Submitted for Review */}
                      {submission.submitted_at &&
                        submission.created_at &&
                        submission.submitted_at !== submission.created_at && (
                          <div className="relative flex gap-4 pb-6">
                            <div className="flex shrink-0 flex-col items-center">
                              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/20">
                                <FileText className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                              </div>
                              {/* Only show line if there's a review status item */}
                              {(submission.status === "approved" ||
                                submission.status === "rejected") && (
                                <div className="absolute left-1/2 top-10 h-6 w-0.5 -translate-x-1/2 bg-border" />
                              )}
                            </div>
                            <div className="flex-1 pb-6">
                              <p className="font-semibold">
                                Submitted for Review
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {formatDateTime(submission.submitted_at)}
                              </p>
                            </div>
                          </div>
                        )}

                      {/* Review Status - Show if status is approved or rejected */}
                      {(submission.status === "approved" ||
                        submission.status === "rejected") && (
                        <div className="relative flex gap-4">
                          <div className="flex shrink-0 flex-col items-center">
                            <div
                              className={`flex h-10 w-10 items-center justify-center rounded-full ${
                                submission.status === "approved"
                                  ? "bg-green-100 dark:bg-green-900/20"
                                  : "bg-red-100 dark:bg-red-900/20"
                              }`}
                            >
                              {submission.status === "approved" ? (
                                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                              ) : (
                                <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                              )}
                            </div>
                            {/* No line after the last item */}
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold capitalize">
                              {submission.status === "approved"
                                ? "Approved"
                                : "Rejected"}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {submission.reviewed_at
                                ? formatDateTime(submission.reviewed_at)
                                : submission.rawData?.reviewed_on
                                  ? formatDateTime(
                                      submission.rawData.reviewed_on
                                    )
                                  : submission.rawData?.updated_on
                                    ? formatDateTime(
                                        submission.rawData.updated_on
                                      )
                                    : submission.rawData?.updated_at
                                      ? formatDateTime(
                                          submission.rawData.updated_at
                                        )
                                      : "N/A"}
                            </p>
                            {submission.admin_comments && (
                              <p className="mt-2 text-sm italic text-muted-foreground">
                                &quot;{submission.admin_comments}&quot;
                              </p>
                            )}
                            {submission.score && (
                              <p className="mt-2 text-lg font-bold text-primary">
                                Score: {submission.score}/100
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </div>
        </Tabs>
      </div>
    </SchoolLayout>
  );
};

export default SchoolSubmissionDetail;
